import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Building2, LayoutDashboard, ClipboardList, Settings2 } from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';

interface ShellProps {
  children: ReactNode;
}

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/companies', icon: Building2, label: 'Companies' },
  { to: '/orders', icon: ClipboardList, label: 'Orders' },
  { to: '/settings', icon: Settings2, label: 'Settings' },
];

export default function Shell({ children }: ShellProps) {
  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        {/* Logo */}
        <div
          style={{
            height: 'var(--header-height)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 var(--space-4)',
            borderBottom: '1px solid var(--color-sidebar-border)',
            gap: 'var(--space-2)',
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-brand)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="3" cy="7" r="2" fill="white" />
              <circle cx="7" cy="7" r="2" fill="white" opacity="0.7" />
              <circle cx="11" cy="7" r="2" fill="white" opacity="0.4" />
            </svg>
          </div>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--weight-light)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--color-text)',
            }}
          >
            mayksel
          </span>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: 'var(--space-3) var(--space-2)' }}>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                fontWeight: isActive ? 'var(--weight-medium)' : 'var(--weight-regular)',
                color: isActive ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
                marginBottom: 'var(--space-1)',
                transition: `background var(--duration-snap) var(--ease-out),
                             color var(--duration-snap) var(--ease-out)`,
                textDecoration: 'none',
              })}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User button */}
        <div
          style={{
            padding: 'var(--space-4)',
            borderTop: '1px solid var(--color-sidebar-border)',
          }}
        >
          <UserButton />
        </div>
      </aside>

      <main className="app-main">
        <header className="app-header" />
        <div className="page-content page-enter">{children}</div>
      </main>
    </div>
  );
}
